// Pure helpers for the System page (goals/admin-panel/DESIGN.md "System
// page"): sizes, ages, what each queue and worker is, and when one looks
// stuck. States are always written out, never shown by colour alone.
import type { AdminSchemas } from "@10twenty/api/admin";
import type { StatusItem, StatusState } from "./analytics";

export type SystemHealth = AdminSchemas["SystemHealth"];
export type SystemQueue = NonNullable<SystemHealth["queues"]>[number];
export type SystemRun = NonNullable<SystemHealth["runs"]>[number];
type QueueKey = SystemQueue["key"];
type Worker = SystemRun["worker"];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/** 512 B / 1.4 KB / 23 MB / 1.2 GB, in powers of 1,024 (as the media quota's megabytes are). */
export function bytes(value: number, locale?: string): string {
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let scaled = value;
  let unit = 0;
  while (Math.abs(scaled) >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit++;
  }
  const digits = unit === 0 || Math.abs(scaled) >= 100 ? 0 : 1;
  return `${scaled.toLocaleString(locale, { maximumFractionDigits: digits })} ${units[unit]}`;
}

/** A duration as its largest whole unit: "40 s", "12 min", "3 h", "2 d". */
export function ageText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86_400)} d`;
}

/** How long ago `at` was, from `now` ("—" without a time). */
export function agoText(at: string | null, now: number): string {
  return at === null ? "—" : `${ageText(now - Date.parse(at))} ago`;
}

export interface QueueInfo {
  label: string;
  /** What fills it and what empties it. */
  note: string;
  /** The oldest due item may wait this long before the queue looks stuck. */
  warnAfterMs: number;
}

// The dispatcher polls every 5 s; a few minutes behind means it is stuck or failing.
export const QUEUE_INFO: Record<QueueKey, QueueInfo> = {
  place_events: { label: "Place alerts", note: "Arrivals and departures to push", warnAfterMs: 5 * MINUTE },
  check_ins: { label: "Check-ins", note: "Check-ins to push", warnAfterMs: 5 * MINUTE },
  member_alerts: {
    label: "Member alerts",
    note: "Low battery, location stale or off, speeding",
    warnAfterMs: 5 * MINUTE,
  },
  supervision: { label: "Supervision", note: "Supervision requests, notices and updates", warnAfterMs: 5 * MINUTE },
  event_expiry: { label: "Event expiry", note: "Warnings before an ended event is deleted", warnAfterMs: 5 * MINUTE },
  sos_pushes: { label: "SOS pushes", note: "Retried with backoff while the alert is open", warnAfterMs: 2 * MINUTE },
  sos_emails: { label: "SOS emails", note: "Emergency contact links, retried while open", warnAfterMs: 2 * MINUTE },
  chat_pushes: { label: "Chat pushes", note: "One per recipient and conversation", warnAfterMs: 5 * MINUTE },
  early_access_emails: {
    label: "Early access emails",
    note: "Confirmation links; held past 200 an hour",
    warnAfterMs: HOUR,
  },
  media_garbage: { label: "Media deletions", note: "Blobs of deleted media for the sweeper", warnAfterMs: HOUR },
  daily_stats: {
    label: "Daily stats",
    note: "Days to rebuild, 15 minutes after the first change",
    warnAfterMs: 15 * MINUTE,
  },
};

/** "warning" once the oldest due item has waited past its queue's limit. */
export function queueState(queue: SystemQueue, now: number): StatusState {
  if (queue.due === 0) return "ok";
  // Due without a time: daily stats marked by a backfill, due at once.
  if (queue.oldest_due_at === null) return "warning";
  return now - Date.parse(queue.oldest_due_at) > QUEUE_INFO[queue.key].warnAfterMs ? "warning" : "ok";
}

export interface WorkerInfo {
  label: string;
  schedule: string;
  /** A success older than this looks stale. */
  staleAfterMs: number;
}

export const WORKER_INFO: Record<Worker, WorkerInfo> = {
  retention: { label: "Retention", schedule: "Hourly, 30 s after start", staleAfterMs: 2 * HOUR + 5 * MINUTE },
  daily_stats: { label: "Daily stats", schedule: "Every minute", staleAfterMs: 10 * MINUTE },
  analytics: { label: "Analytics rollup", schedule: "At 20 past every hour", staleAfterMs: 2 * HOUR + 5 * MINUTE },
};

export function runState(run: SystemRun, now: number): { state: StatusState; text: string } {
  if (run.result === null) return { state: "warning", text: "No run recorded" };
  if (run.result === "failed") return { state: "failing", text: "Failed" };
  if (run.last_ok_at !== null && now - Date.parse(run.last_ok_at) > WORKER_INFO[run.worker].staleAfterMs)
    return { state: "warning", text: "Stale" };
  return { state: "ok", text: "OK" };
}

/** A run's counts as "partitions 2 · sessions 4", leaving zeros out. */
export function runDetail(detail: Record<string, number>): string {
  const parts = Object.entries(detail)
    .filter(([, value]) => value !== 0)
    .map(
      ([key, value]) =>
        `${key
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replaceAll("_", " ")
          .toLowerCase()} ${value}`,
    );
  return parts.length ? parts.join(" · ") : "Nothing to do";
}

/** Sent of all attempts (null without any). */
export function successShare(counts: Record<string, number | string>, sent = "sent"): number | null {
  const total = Object.values(counts).reduce<number>((sum, value) => sum + (typeof value === "number" ? value : 0), 0);
  return total === 0 ? null : Number(counts[sent]) / total;
}

export const CHANNEL_LABELS: Record<string, string> = { fcm: "Firebase (Android)", webpush: "Web Push" };
export const EMAIL_LABELS: Record<string, string> = {
  sos: "SOS links",
  invite: "Emergency contact invitations",
  early_access: "Early access confirmations",
  report: "Report notices",
};

/** Under a tenth of the disk free is worth a look. */
export function diskState(disk: { free_bytes: number; total_bytes: number } | null): StatusState {
  if (disk === null || disk.total_bytes === 0) return "warning";
  return disk.free_bytes / disk.total_bytes < 0.1 ? "warning" : "ok";
}

/** The page's summary strip. */
export function systemStatus(health: SystemHealth, now: number): StatusItem[] {
  const unread = { state: "warning" as const, text: "Unreadable" };
  const items: StatusItem[] = [
    {
      label: "Database",
      state: health.readiness.database === "ok" ? "ok" : "failing",
      text: health.readiness.database === "ok" ? "OK" : "Failing",
    },
  ];
  const { migrations, partitions, queues, runs, media } = health;
  // Readiness knows whether migrations are pending even when the list could not be read.
  if (migrations === null)
    items.push({
      label: "Migrations",
      ...(health.readiness.migrations === "ok"
        ? { state: "ok", text: "Up to date" }
        : { state: "failing", text: "Pending" }),
    });
  else {
    const pending = migrations.pending.length;
    items.push({
      label: "Migrations",
      state: pending === 0 ? "ok" : "failing",
      text: pending === 0 ? "Up to date" : `${pending} pending`,
    });
  }
  items.push({
    label: "Partitions",
    ...(partitions === null
      ? unread
      : partitions.today && partitions.tomorrow
        ? { state: "ok", text: "Ready" }
        : partitions.today
          ? { state: "warning", text: "Tomorrow's missing" }
          : { state: "failing", text: "Today's missing" }),
  });
  const stuck = queues?.filter((queue) => queueState(queue, now) !== "ok").length ?? 0;
  items.push({
    label: "Queues",
    ...(queues === null
      ? unread
      : stuck === 0
        ? { state: "ok", text: "Flowing" }
        : { state: "warning", text: `${stuck} behind` }),
  });
  const states = runs?.map((run) => runState(run, now).state) ?? [];
  items.push({
    label: "Workers",
    ...(runs === null
      ? unread
      : states.includes("failing")
        ? { state: "failing", text: "A run failed" }
        : states.includes("warning")
          ? { state: "warning", text: "Check runs" }
          : { state: "ok", text: "OK" }),
  });
  if (health.unavailable.includes("media")) items.push({ label: "Media disk", ...unread });
  else if (media) {
    const disk = media.disk;
    items.push({
      label: "Media disk",
      state: diskState(disk),
      text: disk ? `${bytes(disk.free_bytes)} free` : "Unreadable",
    });
  }
  return items;
}
